/**
 * Admin Assessment DTOs - Matching backend AdminAssessment DTOs
 * These DTOs represent assessment definitions and requests for backend integration
 */

/**
 * AdminPreLoad DTO - Represents preload configuration data
 */
export interface AdminPreLoadDto {
  name: string;
  value: string;
  type?: string;
}

/**
 * AdminReportSection DTO - Individual report section definition
 */
export interface AdminReportSectionDto {
  dName: string;
  reportSectionId: string;
  reportScript: string;
  reportSectionPreloads: AdminPreLoadDto[];
}

/**
 * AdminSubReport DTO - Sub-report with multiple sections
 */
export interface AdminSubReportDto {
  dName: string;
  reportSections: AdminReportSectionDto[];
}

/**
 * AdminAssessmentReport DTO - Report definition for assessments
 */
export interface AdminAssessmentReportDto {
  dName: string;
  targetType: string;
  subReports: AdminSubReportDto[];
}

/**
 * Complete AdminAssessment DTO with hierarchical structure including reports
 */
export interface AdminAssessmentDto {
  dName: string;
  surveybList: string;
  surveyId: string; // Links with AdminResponseDto.recSurvey
  sourceId: string;
  languageTypeId: string;
  extra: string;
  reports: AdminAssessmentReportDto[];
}

/**
 * Complete AdminAssessmentList DTO with hierarchical structure
 */
export interface AdminAssessmentListDto {
  assessments: AdminAssessmentDto[];
}

/**
 * Complete AdminRequest DTO with hierarchical structure
 */
export interface AdminRequestDto {
  launchqueueId: string;
  launchqueueData: string;
  profileId: string;
  dueDate: string; // ISO string format
  terminate: string; // ISO string format
  status: string;
  assessment: AdminAssessmentDto;
}

/**
 * Complete AdminRequestList DTO with hierarchical structure
 */
export interface AdminRequestListDto {
  adminRequests: AdminRequestDto[];
}

/**
 * Complete AdminLaunchRequest DTO with hierarchical structure
 */
export interface AdminLaunchRequestDto {
  launchqueueId: string;
  launchqueueData: string;
  profileId: string;
  dueDate: string; // ISO string format
  terminate: string; // ISO string format
  status: string;
  assessment: AdminAssessmentDto;
  preloadOverrides: AdminPreLoadDto[];
  reportPreloadOverrides: AdminPreLoadDto[];
}

/**
 * Complete AdminLaunchRequestList DTO with hierarchical structure
 */
export interface AdminLaunchRequestListDto {
  adminRequests: AdminLaunchRequestDto[];
}

/**
 * Helper functions for AdminAssessment operations
 */

/**
 * Create a new AdminPreLoadDto
 */
export const createAdminPreLoadDto = (
  name: string,
  value: string,
  type?: string
): AdminPreLoadDto => ({
  name,
  value,
  type
});

/**
 * Create a new AdminReportSectionDto
 */
export const createAdminReportSectionDto = (
  dName: string,
  reportSectionId: string,
  reportScript: string,
  reportSectionPreloads: AdminPreLoadDto[] = []
): AdminReportSectionDto => ({
  dName,
  reportSectionId,
  reportScript,
  reportSectionPreloads
});

/**
 * Create a new AdminSubReportDto
 */
export const createAdminSubReportDto = (
  dName: string,
  reportSections: AdminReportSectionDto[] = []
): AdminSubReportDto => ({
  dName,
  reportSections
});

/**
 * Create a new AdminAssessmentReportDto
 */
export const createAdminAssessmentReportDto = (
  dName: string,
  targetType: string,
  subReports: AdminSubReportDto[] = []
): AdminAssessmentReportDto => ({
  dName,
  targetType,
  subReports
});

/**
 * Create a new AdminAssessmentDto
 */
export const createAdminAssessmentDto = (
  dName: string,
  surveybList: string,
  surveyId: string = 'ASSESSMENT', // Links with AdminResponseDto.recSurvey
  sourceId: string,
  languageTypeId: string = '',
  extra: string = '',
  reports: AdminAssessmentReportDto[] = []
): AdminAssessmentDto => ({
  dName,
  surveybList,
  surveyId,
  sourceId,
  languageTypeId,
  extra,
  reports
});

/**
 * Create a new AdminRequestDto
 */
export const createAdminRequestDto = (
  launchqueueId: string,
  profileId: string,
  assessment: AdminAssessmentDto,
  status: string = 'PENDING',
  launchqueueData: string = '',
  dueDate?: string,
  terminate?: string
): AdminRequestDto => ({
  launchqueueId,
  launchqueueData,
  profileId,
  dueDate: dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Default 7 days from now
  terminate: terminate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // Default 30 days from now
  status,
  assessment
});

/**
 * Create empty assessment list - assessments should now be downloaded as AdminAssessment content
 * Hardcoded assessments removed since real AdminAssessmentDto definitions can be downloaded
 */
export const createDefaultAssessmentList = (): AdminAssessmentListDto => ({
  assessments: [
    // No hardcoded assessments - download AdminAssessment content instead
  ]
});

/**
 * Assessment status constants
 */
export const ASSESSMENT_STATUS = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED'
} as const;

/**
 * Assessment source type constants  
 */
export const ASSESSMENT_SOURCE_TYPE = {
  ASSESSMENT: 'ASSESSMENT',
  SCREENING: 'SCREENING',
  FOLLOWUP: 'FOLLOWUP'
} as const;

export type AssessmentStatus = typeof ASSESSMENT_STATUS[keyof typeof ASSESSMENT_STATUS];
export type AssessmentSourceType = typeof ASSESSMENT_SOURCE_TYPE[keyof typeof ASSESSMENT_SOURCE_TYPE];